import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  Progress,
} from '@backstage/core-components';
import { useApi, configApiRef, identityApiRef, googleAuthApiRef } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { Entity } from '@backstage/catalog-model';
import {
  makeStyles,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Link,
  Collapse,
  List,
  Divider,
  Tabs,
  Tab,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
} from '@material-ui/core';
import {
  ExpandMore as ExpandMoreIcon,
  Folder as FolderIcon,
  Description as ProjectIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Business as OrgIcon,
  OpenInNew as OpenInNewIcon,
  Storage as ResourceIcon,
  AccountTree as SystemIcon,
  MenuBook as DocsIcon,
  Link as LinkIcon,
  ExpandLess as ExpandLessIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  ViewList as ViewListIcon,
  AccountTreeOutlined as HierarchyIcon,
} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import { homepageGcpProjectsApiRef } from '../../api';
import { GcpOrganization, ProjectBillingInfo } from '../../types';

// GCP Cloud Icon SVG component
const GcpIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ marginRight: 4 }}
  >
    <path d="M12.19 2.38a9.344 9.344 0 0 0-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.11-.247 10.941l.006-.007-.007.03a6.717 6.717 0 0 0 4.077 1.356h5.173l.03.03h5.192c6.687.053 9.376-8.605 3.835-12.35a9.365 9.365 0 0 0-8.825-6.893zm0 1.867a7.47 7.47 0 0 1 7.029 5.476l.217.783.835.174c4.818 1.006 5.934 7.505 1.508 9.398l-.06.028h-4.337l-.03-.03H12.19l-.032.03H7.816a4.85 4.85 0 0 1-2.941-.977l-.023-.018.018-.01c-2.593-1.98-2.556-5.89.317-7.62l.65-.393-.15-.725A7.467 7.467 0 0 1 12.19 4.247z"/>
  </svg>
);

// Backstage Icon SVG component
const BackstageIcon = ({ size = 16 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    style={{ marginRight: 4 }}
  >
    <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
  </svg>
);

const useStyles = makeStyles((theme) => ({
  root: {
    flexGrow: 1,
    width: '100%',
  },
  accordion: {
    marginBottom: theme.spacing(1),
  },
  accordionSummary: {
    backgroundColor: theme.palette.background.default,
  },
  accordionDetails: {
    padding: 0,
  },
  folderHeader: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
  },
  icon: {
    marginRight: theme.spacing(1),
  },
  projectName: {
    flexGrow: 1,
  },
  chip: {
    marginLeft: theme.spacing(1),
    height: 20,
  },
  labelChip: {
    marginLeft: theme.spacing(0.5),
    height: 18,
    fontSize: '0.65rem',
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  authAlert: {
    marginBottom: theme.spacing(2),
  },
  signInButton: {
    marginTop: theme.spacing(1),
  },
  listItem: {
    paddingLeft: theme.spacing(3),
    flexDirection: 'column',
    alignItems: 'stretch',
  },
  nestedAccordion: {
    boxShadow: 'none',
    '&:before': {
      display: 'none',
    },
    marginLeft: theme.spacing(2),
  },
  loadingIndicator: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  loadingSpinner: {
    marginRight: theme.spacing(1),
  },
  performanceChip: {
    marginLeft: theme.spacing(1),
    height: 18,
    fontSize: '0.7rem',
  },
  projectActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  actionLink: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1),
    borderRadius: theme.shape.borderRadius,
    textDecoration: 'none',
    fontSize: '0.75rem',
    fontWeight: 500,
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  consoleLink: {
    color: '#4285F4',
    border: '1px solid #4285F4',
  },
  catalogLink: {
    color: '#36BAA2',
    border: '1px solid #36BAA2',
  },
  catalogLinkActive: {
    color: '#36BAA2',
    border: '1px solid #36BAA2',
  },
  catalogLinkInactive: {
    color: '#999',
    border: '1px solid #999',
    opacity: 0.6,
  },
  labelsContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    marginTop: theme.spacing(0.5),
  },
  projectContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
  },
  projectHeader: {
    display: 'flex',
    alignItems: 'center',
  },
  projectRow: {
    display: 'flex',
    alignItems: 'flex-start',
    width: '100%',
    padding: theme.spacing(1, 0),
  },
  projectMain: {
    display: 'flex',
    alignItems: 'flex-start',
    flex: 1,
  },
  catalogPanel: {
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(2),
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(4),
    borderLeft: `3px solid ${theme.palette.primary.main}`,
    borderRadius: theme.shape.borderRadius,
  },
  catalogSection: {
    marginBottom: theme.spacing(2),
  },
  catalogSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  catalogItem: {
    cursor: 'pointer',
    borderRadius: theme.shape.borderRadius,
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  catalogItemText: {
    '& .MuiListItemText-primary': {
      fontSize: '0.875rem',
    },
    '& .MuiListItemText-secondary': {
      fontSize: '0.75rem',
    },
  },
  noCatalogMatch: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  catalogToggleButton: {
    marginLeft: theme.spacing(1),
    padding: theme.spacing(0.5),
    minWidth: 'auto',
    fontSize: '0.7rem',
  },
  externalLinkChip: {
    margin: theme.spacing(0.5),
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.palette.action.hover,
    },
  },
  linksContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
  },
  techdocsButton: {
    marginTop: theme.spacing(1),
  },
  viewControls: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
  },
  tabs: {
    minHeight: 36,
    '& .MuiTab-root': {
      minHeight: 36,
      padding: theme.spacing(0.5, 2),
    },
  },
  sortControls: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  sortSelect: {
    minWidth: 150,
    '& .MuiSelect-select': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  sortDirectionButton: {
    padding: theme.spacing(0.5),
  },
  billingInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(0.5),
    flexWrap: 'wrap',
  },
  billingItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  billingLabel: {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
  },
  billingValue: {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  billingChange: {
    fontSize: '0.7rem',
    display: 'flex',
    alignItems: 'center',
  },
  billingChangePositive: {
    color: theme.palette.error.main,
  },
  billingChangeNegative: {
    color: theme.palette.success.main,
  },
  flatListContainer: {
    maxHeight: 'calc(100vh - 500px)',
    overflow: 'auto',
  },
  projectCount: {
    color: theme.palette.text.secondary,
    marginLeft: theme.spacing(1),
  },
  orgSelector: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1.5),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
  orgControl: {
    minWidth: 200,
  },
  orgInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
}));

interface GcpResource {
  id: string;
  name: string;
  type: 'organization' | 'folder' | 'project';
  parent?: string;
  state?: string;
  createTime?: string;
  displayName?: string;
  children?: GcpResource[];
  projectCount?: number;
  folderCount?: number;
  labels?: Record<string, string>;
}

interface LoadingState {
  [folderId: string]: boolean;
}

interface LoadedState {
  [folderId: string]: boolean;
}

interface EntityLink {
  url: string;
  title?: string;
  icon?: string;
  type?: string;
  source: 'entity' | 'dependency';
  sourceEntityName?: string;
}

interface CatalogEntityInfo {
  entity: Entity;
  entityRef: string;
  parentEntities: Entity[];
  dependencyEntities: Entity[];
  links: EntityLink[];
  techdocsUrl: string | null;
}

interface CatalogState {
  [projectId: string]: {
    loading: boolean;
    error: string | null;
    data: CatalogEntityInfo | null;
    notFound: boolean;
  };
}

interface CatalogExistsState {
  [projectId: string]: {
    checked: boolean;
    exists: boolean;
    entityRef: string | null;
  };
}

interface BillingState {
  [projectId: string]: ProjectBillingInfo;
}

type GcpProjectSortField = 'projectId' | 'currentCost' | 'previousCost' | 'projectedCost' | 'displayName';
type SortDirection = 'asc' | 'desc';
type ViewMode = 'hierarchy' | 'flat';

export interface GcpHierarchyViewProps {
  /** Selected organization ID (if provided, limits to this org) */
  selectedOrgId?: string;
  /** Callback when org selection changes */
  onOrgChange?: (orgId: string) => void;
  /** Available organizations to select from */
  organizations?: GcpOrganization[];
  /** Whether to show the org selector */
  showOrgSelector?: boolean;
  /** Pre-fetched Google access token */
  googleAccessToken?: string | null;
  /** Compact mode for embedding */
  compact?: boolean;
  /** Tree-only mode - hides view mode tabs, header controls, shows only search + tree */
  treeOnly?: boolean;
}

export const GcpHierarchyView = ({
  selectedOrgId: externalOrgId,
  onOrgChange,
  organizations: externalOrgs,
  showOrgSelector = true,
  googleAccessToken: externalToken,
  compact = false,
  treeOnly = false,
}: GcpHierarchyViewProps) => {
  const classes = useStyles();
  const config = useApi(configApiRef);
  const identityApi = useApi(identityApiRef);
  const googleAuthApi = useApi(googleAuthApiRef);
  const catalogApi = useApi(catalogApiRef);
  const gcpProjectsApi = useApi(homepageGcpProjectsApiRef);

  const [resources, setResources] = useState<GcpResource[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedPanels, setExpandedPanels] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredResources, setFilteredResources] = useState<GcpResource[]>([]);
  const [needsGoogleAuth, setNeedsGoogleAuth] = useState(false);
  const [googleToken, setGoogleToken] = useState<string | null>(externalToken || null);
  const [loadingFolders, setLoadingFolders] = useState<LoadingState>({});
  const [loadedFolders, setLoadedFolders] = useState<LoadedState>({});
  const [initialLoadTime, setInitialLoadTime] = useState<number | null>(null);
  const [fromCache, setFromCache] = useState(false);

  // Organization state
  const [organizations, setOrganizations] = useState<GcpOrganization[]>(externalOrgs || []);
  const [selectedOrgId, setSelectedOrgId] = useState<string>(externalOrgId || '');
  const [loadingOrgs, setLoadingOrgs] = useState(false);

  // Catalog panel state
  const [expandedCatalogPanels, setExpandedCatalogPanels] = useState<string[]>([]);
  const [catalogState, setCatalogState] = useState<CatalogState>({});
  const [catalogExists, setCatalogExists] = useState<CatalogExistsState>({});

  // View mode, sorting, and billing state
  const [viewMode, setViewMode] = useState<ViewMode>('hierarchy');
  const [sortField, setSortField] = useState<GcpProjectSortField>('projectId');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [billingData, setBillingData] = useState<BillingState>({});
  const [billingLoading, setBillingLoading] = useState(false);

  // Refs
  const tokenRef = useRef<string | null>(null);
  const backstageTokenRef = useRef<string | null>(null);
  const loadingFoldersRef = useRef<Set<string>>(new Set());
  const loadedFoldersRef = useRef<Set<string>>(new Set());

  const appBaseUrl = config.getString('app.baseUrl');

  // Build API URL
  const getApiUrl = useCallback((endpoint: string) => {
    const isLocalDev = window.location.hostname === 'localhost';
    return isLocalDev
      ? `http://localhost:7007/api/gcp-resources${endpoint}`
      : `/api/gcp-resources${endpoint}`;
  }, []);

  // Format currency
  const formatCurrency = useCallback((amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  }, []);

  // Calculate percent change
  const getPercentChange = useCallback((current: number, previous: number) => {
    if (previous === 0) return current > 0 ? 100 : 0;
    return ((current - previous) / previous) * 100;
  }, []);

  // Build catalog URL
  const getCatalogEntityUrl = useCallback((entity: Entity) => {
    const namespace = entity.metadata.namespace || 'default';
    const kind = entity.kind.toLowerCase();
    const name = entity.metadata.name;
    return `${appBaseUrl}/catalog/${namespace}/${kind}/${name}`;
  }, [appBaseUrl]);

  // Build TechDocs URL
  const getTechDocsUrl = useCallback((entity: Entity): string | null => {
    const techdocsRef = entity.metadata.annotations?.['backstage.io/techdocs-ref'];
    if (!techdocsRef) return null;
    const namespace = entity.metadata.namespace || 'default';
    const kind = entity.kind.toLowerCase();
    const name = entity.metadata.name;
    return `${appBaseUrl}/docs/${namespace}/${kind}/${name}`;
  }, [appBaseUrl]);

  // Fetch catalog entity
  const fetchCatalogEntity = useCallback(async (projectId: string): Promise<Entity | null> => {
    const gcpAnnotationValue = `projects/${projectId}`;
    try {
      const response = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.type': 'gcp-project',
          'metadata.annotations.gcp.io/id': gcpAnnotationValue,
        },
      });
      return response.items.length > 0 ? response.items[0] : null;
    } catch (err) {
      console.error(`Error fetching catalog entity for ${projectId}:`, err);
      return null;
    }
  }, [catalogApi]);

  // Fetch parent entities
  const fetchParentEntities = useCallback(async (entityRef: string): Promise<Entity[]> => {
    try {
      const ancestors = await catalogApi.getEntityAncestors({ entityRef });
      return ancestors.items
        .map(item => item.entity)
        .filter(e => `${e.kind.toLowerCase()}:${e.metadata.namespace || 'default'}/${e.metadata.name}` !== entityRef);
    } catch (err) {
      console.error(`Error fetching ancestors for ${entityRef}:`, err);
      return [];
    }
  }, [catalogApi]);

  // Fetch dependency entities
  const fetchDependencyEntities = useCallback(async (entity: Entity): Promise<Entity[]> => {
    const dependsOn = entity.spec?.dependsOn as string[] | string | undefined;
    if (!dependsOn) return [];
    const depRefs = Array.isArray(dependsOn) ? dependsOn : [dependsOn];
    const dependencies = await Promise.all(
      depRefs.map(async (ref) => {
        try {
          let kind = 'component';
          let namespace = 'default';
          let name = ref;
          if (ref.includes(':')) {
            const [kindPart, rest] = ref.split(':');
            kind = kindPart;
            if (rest.includes('/')) {
              [namespace, name] = rest.split('/');
            } else {
              name = rest;
            }
          } else if (ref.includes('/')) {
            [namespace, name] = ref.split('/');
          }
          const result = await catalogApi.getEntityByRef(`${kind}:${namespace}/${name}`);
          return result || null;
        } catch {
          return null;
        }
      })
    );
    return dependencies.filter((d): d is Entity => d !== null);
  }, [catalogApi]);

  // Aggregate links
  const aggregateLinks = useCallback((entity: Entity, dependencies: Entity[]): EntityLink[] => {
    const links: EntityLink[] = [];
    if (entity.metadata.links) {
      links.push(...entity.metadata.links.map(link => ({
        ...link,
        source: 'entity' as const,
      })));
    }
    dependencies.forEach(dep => {
      if (dep.metadata.links) {
        links.push(...dep.metadata.links.map(link => ({
          ...link,
          source: 'dependency' as const,
          sourceEntityName: dep.metadata.title || dep.metadata.name,
        })));
      }
    });
    return links;
  }, []);

  // Check catalog exists
  const checkCatalogExists = useCallback(async (projectId: string) => {
    if (catalogExists[projectId]?.checked) return;
    const gcpAnnotationValue = `projects/${projectId}`;
    try {
      const response = await catalogApi.getEntities({
        filter: {
          kind: 'Component',
          'spec.type': 'gcp-project',
          'metadata.annotations.gcp.io/id': gcpAnnotationValue,
        },
        fields: ['metadata.name', 'metadata.namespace', 'kind'],
      });
      const entity = response.items[0];
      setCatalogExists(prev => ({
        ...prev,
        [projectId]: {
          checked: true,
          exists: !!entity,
          entityRef: entity
            ? `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`
            : null,
        },
      }));
    } catch {
      setCatalogExists(prev => ({
        ...prev,
        [projectId]: { checked: true, exists: false, entityRef: null },
      }));
    }
  }, [catalogApi, catalogExists]);

  // Fetch catalog data
  const fetchCatalogData = useCallback(async (projectId: string) => {
    if (catalogState[projectId]?.loading) return;
    setCatalogState(prev => ({
      ...prev,
      [projectId]: { loading: true, error: null, data: null, notFound: false },
    }));
    try {
      const entity = await fetchCatalogEntity(projectId);
      if (!entity) {
        setCatalogState(prev => ({
          ...prev,
          [projectId]: { loading: false, error: null, data: null, notFound: true },
        }));
        setCatalogExists(prev => ({
          ...prev,
          [projectId]: { checked: true, exists: false, entityRef: null },
        }));
        return;
      }
      const entityRef = `${entity.kind.toLowerCase()}:${entity.metadata.namespace || 'default'}/${entity.metadata.name}`;
      setCatalogExists(prev => ({
        ...prev,
        [projectId]: { checked: true, exists: true, entityRef },
      }));
      const [parentEntities, dependencyEntities] = await Promise.all([
        fetchParentEntities(entityRef),
        fetchDependencyEntities(entity),
      ]);
      const links = aggregateLinks(entity, dependencyEntities);
      const techdocsUrl = getTechDocsUrl(entity);
      setCatalogState(prev => ({
        ...prev,
        [projectId]: {
          loading: false,
          error: null,
          data: { entity, entityRef, parentEntities, dependencyEntities, links, techdocsUrl },
          notFound: false,
        },
      }));
    } catch (err) {
      setCatalogState(prev => ({
        ...prev,
        [projectId]: {
          loading: false,
          error: err instanceof Error ? err.message : 'Failed to fetch catalog data',
          data: null,
          notFound: false,
        },
      }));
    }
  }, [catalogState, fetchCatalogEntity, fetchParentEntities, fetchDependencyEntities, aggregateLinks, getTechDocsUrl]);

  // Toggle catalog panel
  const toggleCatalogPanel = useCallback((projectId: string) => {
    setExpandedCatalogPanels(prev => {
      const isExpanding = !prev.includes(projectId);
      if (isExpanding && !catalogState[projectId]?.data && !catalogState[projectId]?.loading) {
        fetchCatalogData(projectId);
      }
      return isExpanding ? [...prev, projectId] : prev.filter(id => id !== projectId);
    });
  }, [catalogState, fetchCatalogData]);

  // Fetch billing data
  const fetchBillingData = useCallback(async (projectIds: string[]) => {
    if (projectIds.length === 0 || !googleToken) return;
    setBillingLoading(true);
    try {
      const billing = await gcpProjectsApi.getProjectsBilling(projectIds, googleToken);
      setBillingData(prev => ({ ...prev, ...billing }));
    } catch (err) {
      console.error('Error fetching billing data:', err);
    } finally {
      setBillingLoading(false);
    }
  }, [gcpProjectsApi, googleToken]);

  // Collect all project IDs
  const collectAllProjectIds = useCallback((items: GcpResource[]): string[] => {
    const projectIds: string[] = [];
    const traverse = (nodes: GcpResource[]) => {
      nodes.forEach(node => {
        if (node.type === 'project') {
          projectIds.push(node.id);
        }
        if (node.children) {
          traverse(node.children);
        }
      });
    };
    traverse(items);
    return projectIds;
  }, []);

  // Update resource children
  const updateResourceChildren = (
    res: GcpResource[],
    targetId: string,
    newChildren: GcpResource[]
  ): GcpResource[] => {
    return res.map(resource => {
      if (resource.id === targetId) {
        return {
          ...resource,
          children: newChildren,
          projectCount: newChildren.filter(c => c.type === 'project').length,
          folderCount: newChildren.filter(c => c.type === 'folder').length,
        };
      }
      if (resource.children && resource.children.length > 0) {
        return {
          ...resource,
          children: updateResourceChildren(resource.children, targetId, newChildren),
        };
      }
      return resource;
    });
  };

  // Handle Google sign-in
  const handleGoogleSignIn = useCallback(async () => {
    try {
      setLoading(true);
      const token = await googleAuthApi.getAccessToken([
        'https://www.googleapis.com/auth/cloud-platform.read-only',
      ]);
      if (token) {
        setGoogleToken(token);
        tokenRef.current = token;
        setNeedsGoogleAuth(false);
      }
    } catch (authError) {
      console.error('Google sign-in failed:', authError);
      setError('Failed to sign in with Google. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [googleAuthApi]);

  // Check for existing Google token
  useEffect(() => {
    if (externalToken) {
      setGoogleToken(externalToken);
      tokenRef.current = externalToken;
      setNeedsGoogleAuth(false);
      return;
    }

    const checkGoogleAuth = async () => {
      try {
        const token = await googleAuthApi.getAccessToken(
          ['https://www.googleapis.com/auth/cloud-platform.read-only'],
          { optional: true },
        );
        if (token) {
          setGoogleToken(token);
          tokenRef.current = token;
          setNeedsGoogleAuth(false);
        } else {
          setNeedsGoogleAuth(true);
          setLoading(false);
        }
      } catch {
        setNeedsGoogleAuth(true);
        setLoading(false);
      }
    };
    checkGoogleAuth();
  }, [googleAuthApi, externalToken]);

  // Load organizations
  useEffect(() => {
    if (!googleToken || externalOrgs) return;
    const loadOrgs = async () => {
      setLoadingOrgs(true);
      try {
        const orgs = await gcpProjectsApi.getOrganizations(googleToken);
        setOrganizations(orgs);
        if (orgs.length > 0 && !selectedOrgId) {
          setSelectedOrgId(orgs[0].name);
          onOrgChange?.(orgs[0].name);
        }
      } catch (err) {
        console.error('Failed to load organizations:', err);
      } finally {
        setLoadingOrgs(false);
      }
    };
    loadOrgs();
  }, [googleToken, gcpProjectsApi, selectedOrgId, onOrgChange, externalOrgs]);

  // Sync external org changes
  useEffect(() => {
    if (externalOrgId && externalOrgId !== selectedOrgId) {
      setSelectedOrgId(externalOrgId);
    }
  }, [externalOrgId, selectedOrgId]);

  // Fetch hierarchy
  const fetchHierarchy = useCallback(async () => {
    if (!googleToken) return;

    const startTime = performance.now();
    try {
      setLoading(true);
      setError(null);

      // Reset state when fetching
      setLoadedFolders({});
      loadedFoldersRef.current.clear();
      setExpandedPanels([]);

      const { token } = await identityApi.getCredentials();
      backstageTokenRef.current = token || null;
      tokenRef.current = googleToken;

      // Build URL with optional org filter
      const url = selectedOrgId
        ? getApiUrl(`/hierarchy?orgId=${encodeURIComponent(selectedOrgId)}`)
        : getApiUrl('/hierarchy');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` }),
          'X-Gcp-Access-Token': googleToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        if (response.status === 401) {
          setNeedsGoogleAuth(true);
        }
        throw new Error(errorData.error || `Failed to fetch hierarchy: ${response.status}`);
      }

      const data = await response.json();
      const loadTime = Math.round(performance.now() - startTime);
      setInitialLoadTime(loadTime);
      setFromCache(data.fromCache || false);

      const resourceData = data.resources || [];
      setResources(resourceData);
      setFilteredResources(resourceData);

      if (resourceData.length > 0 && resourceData[0].type === 'organization') {
        setExpandedPanels([resourceData[0].id]);
      }
    } catch (err) {
      console.error('Error fetching hierarchy:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch GCP resources');
    } finally {
      setLoading(false);
    }
  }, [googleToken, identityApi, getApiUrl, selectedOrgId]);

  // Fetch folder children
  const fetchFolderChildren = useCallback(async (folderId: string) => {
    if (loadedFoldersRef.current.has(folderId) || loadingFoldersRef.current.has(folderId)) {
      return;
    }

    const gToken = tokenRef.current;
    const backstageToken = backstageTokenRef.current;

    if (!gToken) {
      console.warn('No Google token available');
      return;
    }

    loadingFoldersRef.current.add(folderId);
    setLoadingFolders(prev => ({ ...prev, [folderId]: true }));

    try {
      const encodedFolderId = encodeURIComponent(folderId);
      const response = await fetch(getApiUrl(`/children/${encodedFolderId}`), {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          ...(backstageToken && { 'Authorization': `Bearer ${backstageToken}` }),
          'X-Gcp-Access-Token': gToken,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        console.error(`Failed to fetch children for ${folderId}: ${response.status}`);
        return;
      }

      const data = await response.json();
      setResources(prevResources => {
        return updateResourceChildren(prevResources, folderId, [
          ...data.folders.map((f: GcpResource) => ({
            ...f,
            children: [],
            projectCount: -1,
            folderCount: -1,
          })),
          ...data.projects,
        ]);
      });

      loadedFoldersRef.current.add(folderId);
      setLoadedFolders(prev => ({ ...prev, [folderId]: true }));
    } catch (err) {
      console.error(`Error fetching children for ${folderId}:`, err);
    } finally {
      loadingFoldersRef.current.delete(folderId);
      setLoadingFolders(prev => ({ ...prev, [folderId]: false }));
    }
  }, [getApiUrl]);

  // Initial fetch
  useEffect(() => {
    if (googleToken) {
      fetchHierarchy();
    }
  }, [googleToken, fetchHierarchy]);

  // Fetch billing when resources change
  useEffect(() => {
    if (resources.length === 0 || !googleToken) return;
    const projectIds = collectAllProjectIds(resources);
    if (projectIds.length > 0) {
      fetchBillingData(projectIds);
    }
  }, [resources, collectAllProjectIds, fetchBillingData, googleToken]);

  // Filter resources based on search
  useEffect(() => {
    if (!searchTerm) {
      setFilteredResources(resources);
      return;
    }

    const filterResources = (items: GcpResource[]): GcpResource[] => {
      return items.reduce((acc: GcpResource[], item) => {
        const matchesSearch =
          item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.labels && Object.entries(item.labels).some(([k, v]) =>
            k.toLowerCase().includes(searchTerm.toLowerCase()) ||
            v.toLowerCase().includes(searchTerm.toLowerCase())
          ));

        if (item.children) {
          const filteredChildren = filterResources(item.children);
          if (filteredChildren.length > 0 || matchesSearch) {
            acc.push({ ...item, children: filteredChildren });
          }
        } else if (matchesSearch) {
          acc.push(item);
        }

        return acc;
      }, []);
    };

    setFilteredResources(filterResources(resources));
  }, [searchTerm, resources]);

  // Get all projects as flat list
  const allProjects = useMemo(() => {
    const projects: GcpResource[] = [];
    const traverse = (nodes: GcpResource[]) => {
      nodes.forEach(node => {
        if (node.type === 'project') {
          projects.push(node);
        }
        if (node.children) {
          traverse(node.children);
        }
      });
    };
    traverse(filteredResources);
    return projects;
  }, [filteredResources]);

  // Sort projects
  const sortedProjects = useMemo(() => {
    const sorted = [...allProjects];
    sorted.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'projectId':
          comparison = a.id.localeCompare(b.id);
          break;
        case 'displayName':
          comparison = (a.displayName || a.name).localeCompare(b.displayName || b.name);
          break;
        case 'currentCost':
          comparison = (billingData[a.id]?.currentMonthCost || 0) - (billingData[b.id]?.currentMonthCost || 0);
          break;
        case 'previousCost':
          comparison = (billingData[a.id]?.previousMonthCost || 0) - (billingData[b.id]?.previousMonthCost || 0);
          break;
        case 'projectedCost':
          comparison = (billingData[a.id]?.projectedMonthCost || 0) - (billingData[b.id]?.projectedMonthCost || 0);
          break;
        default:
          comparison = 0;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    return sorted;
  }, [allProjects, sortField, sortDirection, billingData]);

  // Handle accordion expand/collapse
  const handleAccordionChange = (panel: string, isFolder: boolean) =>
    (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      setExpandedPanels(prev => {
        if (isExpanded) {
          if (isFolder && !loadedFolders[panel]) {
            fetchFolderChildren(panel);
          }
          return [...prev, panel];
        }
        return prev.filter(p => p !== panel);
      });
    };

  const handleExpandAll = () => {
    const allPanelIds: string[] = [];
    const collectPanelIds = (items: GcpResource[]) => {
      items.forEach(item => {
        if (item.type === 'folder' || item.type === 'organization') {
          allPanelIds.push(item.id);
        }
        if (item.children) {
          collectPanelIds(item.children);
        }
      });
    };
    collectPanelIds(resources);
    setExpandedPanels(allPanelIds);
  };

  const handleCollapseAll = () => {
    setExpandedPanels([]);
  };

  const openGcpConsole = (projectId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    window.open(
      `https://console.cloud.google.com/home/dashboard?project=${projectId}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleOrgSelect = useCallback((orgId: string) => {
    setSelectedOrgId(orgId);
    onOrgChange?.(orgId);
  }, [onOrgChange]);

  const formatOrgName = (org: GcpOrganization) => {
    return org.displayName || org.name.replace('organizations/', 'Org ');
  };

  // Render labels
  const renderLabels = (labels?: Record<string, string>) => {
    if (!labels || Object.keys(labels).length === 0) return null;
    return (
      <Box className={classes.labelsContainer}>
        {Object.entries(labels).slice(0, 5).map(([key, value]) => (
          <Chip
            key={key}
            label={`${key}: ${value}`}
            size="small"
            variant="outlined"
            className={classes.labelChip}
          />
        ))}
        {Object.keys(labels).length > 5 && (
          <Chip
            label={`+${Object.keys(labels).length - 5} more`}
            size="small"
            variant="outlined"
            className={classes.labelChip}
          />
        )}
      </Box>
    );
  };

  // Render billing info
  const renderBillingInfo = (projectId: string) => {
    const billing = billingData[projectId];
    if (!billing) return null;

    const percentChange = getPercentChange(billing.currentMonthCost, billing.previousMonthCost);
    const isPositiveChange = percentChange > 0;

    return (
      <Box className={classes.billingInfo}>
        <Box className={classes.billingItem}>
          <Typography className={classes.billingLabel}>Previous</Typography>
          <Typography className={classes.billingValue}>
            {formatCurrency(billing.previousMonthCost, billing.currency)}
          </Typography>
        </Box>
        <Box className={classes.billingItem}>
          <Typography className={classes.billingLabel}>Current</Typography>
          <Typography className={classes.billingValue}>
            {formatCurrency(billing.currentMonthCost, billing.currency)}
          </Typography>
          {billing.previousMonthCost > 0 && (
            <Typography
              className={`${classes.billingChange} ${
                isPositiveChange ? classes.billingChangePositive : classes.billingChangeNegative
              }`}
            >
              {isPositiveChange ? '+' : ''}{percentChange.toFixed(1)}%
            </Typography>
          )}
        </Box>
        <Box className={classes.billingItem}>
          <Typography className={classes.billingLabel}>Projected</Typography>
          <Typography className={classes.billingValue}>
            {formatCurrency(billing.projectedMonthCost, billing.currency)}
          </Typography>
        </Box>
        {billing.billingReportUrl && (
          <Tooltip title="View billing report">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                window.open(billing.billingReportUrl, '_blank');
              }}
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  };

  // Render catalog panel
  const renderCatalogPanel = (projectId: string) => {
    const state = catalogState[projectId];
    const isExpanded = expandedCatalogPanels.includes(projectId);

    if (!isExpanded) return null;

    if (state?.loading) {
      return (
        <Box className={classes.catalogPanel}>
          <Box className={classes.loadingIndicator}>
            <CircularProgress size={20} className={classes.loadingSpinner} />
            <Typography variant="body2">Loading catalog information...</Typography>
          </Box>
        </Box>
      );
    }

    if (state?.error) {
      return (
        <Box className={classes.catalogPanel}>
          <Alert severity="error">
            Failed to load catalog information: {state.error}
            <Button size="small" onClick={() => fetchCatalogData(projectId)}>Retry</Button>
          </Alert>
        </Box>
      );
    }

    if (state?.notFound) {
      return (
        <Box className={classes.catalogPanel}>
          <Box className={classes.noCatalogMatch}>
            <Typography variant="body2" color="textSecondary">
              No catalog entity found for this project.
            </Typography>
            <Link
              href={`${appBaseUrl}/catalog?filters[kind]=component&filters[metadata.name]=${projectId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              Search in Catalog
            </Link>
          </Box>
        </Box>
      );
    }

    const catalogInfo = state?.data;
    if (!catalogInfo) return null;

    const hasContent = catalogInfo.parentEntities.length > 0 ||
      catalogInfo.dependencyEntities.length > 0 ||
      catalogInfo.links.length > 0 ||
      catalogInfo.techdocsUrl;

    if (!hasContent) {
      return (
        <Box className={classes.catalogPanel}>
          <Typography variant="body2" color="textSecondary">
            This project exists in the catalog but has no linked resources.
          </Typography>
          <Link
            href={getCatalogEntityUrl(catalogInfo.entity)}
            target="_blank"
            rel="noopener noreferrer"
          >
            View in Catalog
          </Link>
        </Box>
      );
    }

    return (
      <Box className={classes.catalogPanel}>
        {catalogInfo.parentEntities.length > 0 && (
          <Box className={classes.catalogSection}>
            <Typography variant="subtitle2" className={classes.catalogSectionTitle}>
              <SystemIcon fontSize="small" />
              Parent Components
            </Typography>
            <List dense disablePadding>
              {catalogInfo.parentEntities.map(parent => (
                <ListItem
                  key={parent.metadata.name}
                  button
                  className={classes.catalogItem}
                  onClick={() => window.open(getCatalogEntityUrl(parent), '_blank')}
                >
                  <ListItemIcon>
                    <SystemIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.catalogItemText}
                    primary={parent.metadata.title || parent.metadata.name}
                    secondary={`${parent.kind}: ${parent.spec?.type || 'unknown'}`}
                  />
                  <OpenInNewIcon fontSize="small" color="action" />
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        {catalogInfo.dependencyEntities.length > 0 && (
          <Box className={classes.catalogSection}>
            <Typography variant="subtitle2" className={classes.catalogSectionTitle}>
              <ResourceIcon fontSize="small" />
              Linked Resources ({catalogInfo.dependencyEntities.length})
            </Typography>
            <List dense disablePadding>
              {catalogInfo.dependencyEntities.slice(0, 5).map(dep => (
                <ListItem
                  key={dep.metadata.name}
                  button
                  className={classes.catalogItem}
                  onClick={() => window.open(getCatalogEntityUrl(dep), '_blank')}
                >
                  <ListItemIcon>
                    <ResourceIcon fontSize="small" color="action" />
                  </ListItemIcon>
                  <ListItemText
                    className={classes.catalogItemText}
                    primary={dep.metadata.title || dep.metadata.name}
                    secondary={`${dep.kind}: ${(dep.spec as Record<string, unknown>)?.type || 'unknown'}`}
                  />
                  <OpenInNewIcon fontSize="small" color="action" />
                </ListItem>
              ))}
              {catalogInfo.dependencyEntities.length > 5 && (
                <ListItem
                  button
                  onClick={() => window.open(getCatalogEntityUrl(catalogInfo.entity), '_blank')}
                >
                  <ListItemText
                    primary={`View all ${catalogInfo.dependencyEntities.length} resources in catalog...`}
                  />
                </ListItem>
              )}
            </List>
          </Box>
        )}

        {catalogInfo.links.length > 0 && (
          <Box className={classes.catalogSection}>
            <Typography variant="subtitle2" className={classes.catalogSectionTitle}>
              <LinkIcon fontSize="small" />
              External Links ({catalogInfo.links.length})
            </Typography>
            <Box className={classes.linksContainer}>
              {catalogInfo.links.map((link, idx) => (
                <Chip
                  key={idx}
                  label={link.title || link.url}
                  size="small"
                  clickable
                  onClick={() => window.open(link.url, '_blank')}
                  className={classes.externalLinkChip}
                  icon={<OpenInNewIcon fontSize="small" />}
                />
              ))}
            </Box>
          </Box>
        )}

        {catalogInfo.techdocsUrl && (
          <Box className={classes.catalogSection}>
            <Divider style={{ marginBottom: 8 }} />
            <Button
              variant="outlined"
              size="small"
              startIcon={<DocsIcon />}
              onClick={() => window.open(catalogInfo.techdocsUrl!, '_blank')}
              className={classes.techdocsButton}
            >
              View TechDocs
            </Button>
          </Box>
        )}
      </Box>
    );
  };

  // Render flat list
  const renderFlatProjectList = () => {
    return (
      <Box className={classes.flatListContainer}>
        <List>
          {sortedProjects.map((node) => {
            const isCatalogExpanded = expandedCatalogPanels.includes(node.id);
            const catalogExistsInfo = catalogExists[node.id];
            const hasCatalogEntity = catalogExistsInfo?.exists;
            const catalogEntityRef = catalogExistsInfo?.entityRef;

            if (!catalogExistsInfo?.checked) {
              checkCatalogExists(node.id);
            }

            const getCatalogUrl = () => {
              if (catalogEntityRef) {
                const [kindNs, name] = catalogEntityRef.split('/');
                const [kind, namespace] = kindNs.includes(':') ? kindNs.split(':') : ['component', kindNs];
                return `${appBaseUrl}/catalog/${namespace || 'default'}/${kind}/${name}`;
              }
              return `${appBaseUrl}/catalog?filters[kind]=component&filters[metadata.name]=${node.id}`;
            };

            return (
              <Box key={node.id}>
                <ListItem className={classes.listItem}>
                  <Box className={classes.projectRow}>
                    <Box
                      className={classes.projectMain}
                      onClick={() => toggleCatalogPanel(node.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <ListItemIcon>
                        <ProjectIcon color="primary" />
                        {isCatalogExpanded ? (
                          <ExpandLessIcon fontSize="small" style={{ marginLeft: -8 }} />
                        ) : (
                          <ExpandMoreIcon fontSize="small" style={{ marginLeft: -8 }} />
                        )}
                      </ListItemIcon>
                      <Box className={classes.projectContent}>
                        <Box className={classes.projectHeader}>
                          <Typography variant="body1">
                            {node.displayName || node.name}
                          </Typography>
                          {node.state && node.state !== 'ACTIVE' && (
                            <Chip label={node.state} size="small" className={classes.chip} color="secondary" />
                          )}
                        </Box>
                        <Typography variant="caption" color="textSecondary">
                          {node.id}
                        </Typography>
                        {renderBillingInfo(node.id)}
                        {renderLabels(node.labels)}
                      </Box>
                    </Box>
                    <Box className={classes.projectActions}>
                      <Tooltip title="Open in GCP Console">
                        <Link
                          component="button"
                          onClick={(e: React.MouseEvent) => openGcpConsole(node.id, e)}
                          className={`${classes.actionLink} ${classes.consoleLink}`}
                        >
                          <GcpIcon size={14} />
                          Console
                        </Link>
                      </Tooltip>
                      <Tooltip title={hasCatalogEntity ? 'View in Catalog' : (catalogExistsInfo?.checked ? 'No catalog entry' : 'View in Catalog')}>
                        <Link
                          href={getCatalogUrl()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`${classes.actionLink} ${
                            catalogExistsInfo?.checked && !hasCatalogEntity
                              ? classes.catalogLinkInactive
                              : classes.catalogLinkActive
                          }`}
                          onClick={(e: React.MouseEvent) => e.stopPropagation()}
                        >
                          <BackstageIcon size={14} />
                          Catalog
                          <OpenInNewIcon fontSize="small" style={{ marginLeft: 2, width: 12, height: 12 }} />
                        </Link>
                      </Tooltip>
                    </Box>
                  </Box>
                  <Collapse in={isCatalogExpanded} timeout="auto" unmountOnExit style={{ width: '100%' }}>
                    {renderCatalogPanel(node.id)}
                  </Collapse>
                </ListItem>
                <Divider />
              </Box>
            );
          })}
        </List>
      </Box>
    );
  };

  // Render hierarchy accordion
  const renderResourceAccordion = (nodes: GcpResource[], depth: number = 0) => {
    return nodes.map((node) => {
      const isOrganization = node.type === 'organization';
      const isFolder = node.type === 'folder';
      const isExpanded = expandedPanels.includes(node.id);
      const isLoading = loadingFolders[node.id];
      const hasChildren = node.children && node.children.length > 0;
      const needsLoad = isFolder && !loadedFolders[node.id] && (node.folderCount === -1 || node.projectCount === -1);

      if (isOrganization || isFolder) {
        const getIcon = () => {
          if (isOrganization) return <OrgIcon className={classes.icon} color="primary" />;
          return <FolderIcon className={classes.icon} color="action" />;
        };

        const getCountLabel = () => {
          if (isLoading) return 'Loading...';
          if (needsLoad) return 'Click to load';
          const parts = [];
          if (node.projectCount && node.projectCount > 0) {
            parts.push(`${node.projectCount} project${node.projectCount === 1 ? '' : 's'}`);
          }
          if (node.folderCount && node.folderCount > 0) {
            parts.push(`${node.folderCount} folder${node.folderCount === 1 ? '' : 's'}`);
          }
          return parts.join(', ');
        };

        return (
          <Accordion
            key={node.id}
            expanded={isExpanded}
            onChange={handleAccordionChange(node.id, isFolder)}
            className={depth > 0 ? classes.nestedAccordion : classes.accordion}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={classes.accordionSummary}>
              <Box className={classes.folderHeader}>
                {getIcon()}
                <Typography
                  className={classes.projectName}
                  style={isOrganization ? { fontWeight: 'bold', fontSize: '1.1rem' } : {}}
                >
                  {node.displayName || node.name}
                </Typography>
                {isLoading && (
                  <CircularProgress size={16} className={classes.loadingSpinner} />
                )}
                {getCountLabel() && (
                  <Chip
                    label={getCountLabel()}
                    size="small"
                    className={classes.chip}
                    color={needsLoad ? "secondary" : (isOrganization ? "primary" : "default")}
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              <Box width="100%">
                {isLoading && (
                  <Box className={classes.loadingIndicator}>
                    <CircularProgress size={20} className={classes.loadingSpinner} />
                    <Typography variant="body2">Loading folder contents...</Typography>
                  </Box>
                )}
                {hasChildren && renderResourceAccordion(node.children!, depth + 1)}
                {!isLoading && !hasChildren && isExpanded && loadedFolders[node.id] && (
                  <Box className={classes.loadingIndicator}>
                    <Typography variant="body2" color="textSecondary">
                      No items in this folder
                    </Typography>
                  </Box>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        );
      }

      // Render projects
      const isCatalogExpanded = expandedCatalogPanels.includes(node.id);
      const catalogExistsInfo = catalogExists[node.id];
      const hasCatalogEntity = catalogExistsInfo?.exists;
      const catalogEntityRef = catalogExistsInfo?.entityRef;

      if (!catalogExistsInfo?.checked) {
        checkCatalogExists(node.id);
      }

      const getCatalogUrl = () => {
        if (catalogEntityRef) {
          const [kindNs, name] = catalogEntityRef.split('/');
          const [kind, namespace] = kindNs.includes(':') ? kindNs.split(':') : ['component', kindNs];
          return `${appBaseUrl}/catalog/${namespace || 'default'}/${kind}/${name}`;
        }
        return `${appBaseUrl}/catalog?filters[kind]=component&filters[metadata.name]=${node.id}`;
      };

      return (
        <Box key={node.id}>
          <ListItem className={classes.listItem}>
            <Box className={classes.projectRow}>
              <Box
                className={classes.projectMain}
                onClick={() => toggleCatalogPanel(node.id)}
                style={{ cursor: 'pointer' }}
              >
                <ListItemIcon>
                  <ProjectIcon color="primary" />
                  {isCatalogExpanded ? (
                    <ExpandLessIcon fontSize="small" style={{ marginLeft: -8 }} />
                  ) : (
                    <ExpandMoreIcon fontSize="small" style={{ marginLeft: -8 }} />
                  )}
                </ListItemIcon>
                <Box className={classes.projectContent}>
                  <Box className={classes.projectHeader}>
                    <Typography variant="body1">
                      {node.displayName || node.name}
                    </Typography>
                    {node.state && node.state !== 'ACTIVE' && (
                      <Chip label={node.state} size="small" className={classes.chip} color="secondary" />
                    )}
                  </Box>
                  <Typography variant="caption" color="textSecondary">
                    {node.id}
                  </Typography>
                  {renderBillingInfo(node.id)}
                  {renderLabels(node.labels)}
                </Box>
              </Box>
              <Box className={classes.projectActions}>
                <Tooltip title="Open in GCP Console">
                  <Link
                    component="button"
                    onClick={(e: React.MouseEvent) => openGcpConsole(node.id, e)}
                    className={`${classes.actionLink} ${classes.consoleLink}`}
                  >
                    <GcpIcon size={14} />
                    Console
                  </Link>
                </Tooltip>
                <Tooltip title={hasCatalogEntity ? 'View in Catalog' : (catalogExistsInfo?.checked ? 'No catalog entry' : 'View in Catalog')}>
                  <Link
                    href={getCatalogUrl()}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${classes.actionLink} ${
                      catalogExistsInfo?.checked && !hasCatalogEntity
                        ? classes.catalogLinkInactive
                        : classes.catalogLinkActive
                    }`}
                    onClick={(e: React.MouseEvent) => e.stopPropagation()}
                  >
                    <BackstageIcon size={14} />
                    Catalog
                    <OpenInNewIcon fontSize="small" style={{ marginLeft: 2, width: 12, height: 12 }} />
                  </Link>
                </Tooltip>
              </Box>
            </Box>
            <Collapse in={isCatalogExpanded} timeout="auto" unmountOnExit style={{ width: '100%' }}>
              {renderCatalogPanel(node.id)}
            </Collapse>
          </ListItem>
        </Box>
      );
    });
  };

  if (loading && !resources.length) {
    return <Progress />;
  }

  if (needsGoogleAuth) {
    return (
      <Alert
        severity="info"
        action={
          <Button color="inherit" size="small" onClick={handleGoogleSignIn}>
            Sign in with Google
          </Button>
        }
      >
        Sign in with Google to view the GCP resource hierarchy.
      </Alert>
    );
  }

  if (error) {
    return (
      <Alert severity="error">
        {error}
        <Button size="small" onClick={fetchHierarchy} style={{ marginLeft: 8 }}>
          Retry
        </Button>
      </Alert>
    );
  }

  return (
    <Box className={classes.root}>
      {/* Org Selector */}
      {showOrgSelector && organizations.length > 0 && (
        <Box className={classes.orgSelector}>
          <FormControl variant="outlined" size="small" className={classes.orgControl}>
            <InputLabel>Organization</InputLabel>
            <Select
              value={selectedOrgId}
              onChange={(e) => handleOrgSelect(e.target.value as string)}
              label="Organization"
              startAdornment={<OrgIcon fontSize="small" style={{ marginRight: 4 }} />}
            >
              {organizations.map(org => (
                <MenuItem key={org.name} value={org.name}>
                  {formatOrgName(org)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {loadingOrgs && (
            <Box className={classes.orgInfo}>
              <CircularProgress size={16} />
              <Typography variant="caption">Loading organizations...</Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Header - hidden in treeOnly mode */}
      {!treeOnly && (
        <Box className={classes.header}>
          <Box display="flex" alignItems="center">
            {initialLoadTime !== null && (
              <Chip
                label={`Loaded in ${initialLoadTime}ms${fromCache ? ' (cached)' : ''}`}
                size="small"
                className={classes.performanceChip}
                color={initialLoadTime < 1000 ? 'primary' : 'secondary'}
              />
            )}
            {billingLoading && (
              <Chip
                label="Loading billing..."
                size="small"
                className={classes.performanceChip}
                icon={<CircularProgress size={12} />}
              />
            )}
          </Box>
          <Box>
            {viewMode === 'hierarchy' && (
              <>
                <Button size="small" onClick={handleExpandAll} startIcon={<ExpandMoreIcon />}>
                  Expand All
                </Button>
                <Button size="small" onClick={handleCollapseAll} startIcon={<FolderIcon />}>
                  Collapse All
                </Button>
              </>
            )}
            <Tooltip title="Refresh">
              <IconButton onClick={() => {
                setLoadedFolders({});
                loadedFoldersRef.current.clear();
                setCatalogState({});
                setExpandedCatalogPanels([]);
                setBillingData({});
                fetchHierarchy();
              }} size="small">
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}

      {/* View Mode Toggle and Sort Controls - hidden in treeOnly mode */}
      {!treeOnly && (
        <Box className={classes.viewControls}>
          <Tabs
            value={viewMode}
            onChange={(_e, newValue) => setViewMode(newValue as ViewMode)}
            className={classes.tabs}
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab value="hierarchy" label="Hierarchy" icon={<HierarchyIcon />} />
            <Tab value="flat" label={`All Projects (${allProjects.length})`} icon={<ViewListIcon />} />
          </Tabs>

          {viewMode === 'flat' && (
            <Box className={classes.sortControls}>
              <FormControl variant="outlined" size="small" className={classes.sortSelect}>
                <InputLabel>Sort by</InputLabel>
                <Select
                  value={sortField}
                  onChange={(e) => setSortField(e.target.value as GcpProjectSortField)}
                  label="Sort by"
                >
                  <MenuItem value="projectId">Project ID</MenuItem>
                  <MenuItem value="displayName">Display Name</MenuItem>
                  <MenuItem value="currentCost">Current Cost</MenuItem>
                  <MenuItem value="previousCost">Previous Cost</MenuItem>
                  <MenuItem value="projectedCost">Projected Cost</MenuItem>
                </Select>
              </FormControl>
              <Tooltip title={`Sort ${sortDirection === 'asc' ? 'Descending' : 'Ascending'}`}>
                <IconButton
                  size="small"
                  onClick={() => setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc')}
                  className={classes.sortDirectionButton}
                >
                  {sortDirection === 'asc' ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>
      )}

      {/* Search */}
      <TextField
        className={classes.searchField}
        fullWidth
        variant="outlined"
        size={compact ? 'small' : 'medium'}
        placeholder="Search folders, projects, or labels..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
      />

      {/* Content - always show hierarchy in treeOnly mode */}
      {filteredResources.length === 0 ? (
        <Alert severity="info">
          No GCP resources found. This could mean:
          <ul>
            <li>You don't have access to any projects in this organization</li>
            <li>Your Google OAuth token doesn't have the necessary scopes</li>
          </ul>
        </Alert>
      ) : (treeOnly || viewMode === 'hierarchy') ? (
        <Box className={classes.root}>
          {renderResourceAccordion(filteredResources)}
        </Box>
      ) : (
        renderFlatProjectList()
      )}
    </Box>
  );
};
